import type { CompRef } from '@wix/document-services-types';
import {
  Action,
  Data,
  DISPLAY_GROUPS,
  DisplayGroups,
  Presets,
  CssProperties,
  CssCustomProperties,
} from '@wix/component-protocol/schema';
import { applyDisplayFilter } from '../utils/applyDisplayFilter';

import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { ComponentsDefinitionsPlatformService } from './ComponentsDefinitionsPlatformService';
import { EditActionCapabilityResolver } from './editActionCapabilities';
import { NativeActionsList } from '../types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class EditActionsPlatformService extends AbstractComponentsPlatformService {
  private readonly componentsDefinitionsPlatformService: ComponentsDefinitionsPlatformService;

  private readonly editActionCapabilityResolver: EditActionCapabilityResolver;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.componentsDefinitionsPlatformService =
      new ComponentsDefinitionsPlatformService(...args);
    this.editActionCapabilityResolver = new EditActionCapabilityResolver(
      ...args,
    );
  }

  getActionStyleItems(editorPointers: EditorPointer<CompRef>[]): {
    cssProperties: CssProperties;
    cssCustomProperties: CssCustomProperties;
  } {
    return this.editActionCapabilityResolver.getStyleItems(editorPointers);
  }

  hasActionableStyleProperties(editorPointers: EditorPointer<CompRef>[]) {
    return this.editActionCapabilityResolver.hasStyleItems(editorPointers);
  }

  getActionDataItems(
    actionName: keyof typeof NativeActionsList,
    editorPointers: EditorPointer<CompRef>[],
  ): Data[string][] {
    return this.editActionCapabilityResolver.getDataItems(
      actionName,
      editorPointers,
    );
  }

  hasActionableDataItems(
    actionName: keyof typeof NativeActionsList,
    editorPointers: EditorPointer<CompRef>[],
  ): boolean {
    return this.editActionCapabilityResolver.hasDataItems(
      actionName,
      editorPointers,
    );
  }

  private resolvePresetDisplayGroups(
    editorPointers: EditorPointer<CompRef>[],
    presets: Presets,
  ): DisplayGroups {
    const presetsGroups: DisplayGroups =
      this.componentsDefinitionsPlatformService.getDisplayGroups(
        editorPointers,
        { filter: { groupType: DISPLAY_GROUPS.GROUP_TYPE.presets } },
      );

    const filteredEntries: [string, DisplayGroups[string]][] = [];

    Object.entries(presetsGroups).forEach(([key, presetsGroup]) => {
      if (!presetsGroup.presets) {
        return;
      }

      const filteredPresetItems = (presetsGroup.presets.items ?? []).filter(
        (presetKey) => Object.keys(presets).includes(presetKey),
      );

      if (filteredPresetItems.length === 0) {
        return;
      }

      filteredEntries.push([
        key,
        {
          ...presetsGroup,
          presets: {
            ...presetsGroup.presets,
            items: filteredPresetItems,
          },
        },
      ]);
    });

    return Object.fromEntries(filteredEntries);
  }

  getActionPresets(
    actionName: keyof typeof NativeActionsList,
    editorPointers: EditorPointer<CompRef>[],
  ): Presets {
    if (actionName !== NativeActionsList.presets) {
      return {} as Presets;
    }

    const presets =
      this.componentsDefinitionsPlatformService.getPresets(editorPointers);
    const categories = this.resolvePresetDisplayGroups(editorPointers, presets);

    if (!Object.keys(categories).length) {
      return presets;
    }

    const firstGroup = Object.values(categories)[0];
    if (
      Object.keys(categories).length === 1 &&
      !firstGroup?.presets?.items?.length
    ) {
      return {};
    }

    const categorizedPresetKeys: string[] = Object.values(categories).reduce(
      (acc, category) => [...acc, ...(category.presets?.items ?? [])],
      [] as string[],
    );

    return Object.fromEntries(
      Object.entries(presets ?? {}).filter(([presetKey]) =>
        categorizedPresetKeys.includes(presetKey),
      ),
    );
  }

  hasTemplates(editorPointer: EditorPointer<CompRef>) {
    return this.hostAPI.editor.templates.hasTemplates(editorPointer);
  }

  applyDisplayFilters<T extends Record<string, Action>>(
    editorPointers: EditorPointer<CompRef>[],
    actions: T,
  ) {
    const resolver = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: editorPointers as EditorPointer<CompRef>[],
      })
      .getAPI().resolver;

    const appliedDisplayFilters = resolver.getDisplayFilters('actions');

    return applyDisplayFilter(actions, appliedDisplayFilters);
  }
}
